import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { EMPTY, from } from 'rxjs';
import {
  catchError,
  concatMap,
  finalize,
  takeUntil,
  tap,
  toArray,
} from 'rxjs/operators';

import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { GroupCustomSegmentsService } from '@main-client/src/app/group-custom-segments-settings/group-custom-segments.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { UpdateGroupSuccess } from '@libs/src/groups/groups-store/groups.actions';
import {
  Group,
  GroupCustomSegment,
  GroupCustomSegmentTypes,
} from '@libs/src/models/group.model';
import { IAppState } from '@main-client/src/app/app.state';
import { ColumnValue } from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-populate-segment-values-drawer',
  templateUrl: './populate-segment-values-drawer.component.html',
})
export class PopulateSegmentValuesDrawerComponent implements OnInit {
  @Input() values: ColumnValue[];
  @Input() group: Group;
  @Input() isPrivateSegment: boolean;
  @Input() customSegment: GroupCustomSegmentTypes;
  isLoading = false;
  segmentValues: string[] = [];
  segmentValuesToDisplay = new FormControl();

  constructor(
    private readonly store: Store<IAppState>,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly drawerRef: NzDrawerRef<PopulateSegmentValuesDrawerComponent>,
    private readonly groupCustomSegmentsService: GroupCustomSegmentsService,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit() {
    this.segmentValues = this.values.map((value) => value.name).sort();
    this.segmentValuesToDisplay.setValue(this.segmentValues.join('\n'));
  }

  addValues() {
    from(this.segmentValues)
      .pipe(
        concatMap((nodeValue) => {
          this.isLoading = true;
          return this.groupCustomSegmentsService
            .postSegmentsNode(
              this.group,
              this.customSegment,
              { name: nodeValue },
              this.isPrivateSegment,
            )
            .pipe(
              catchError((response: HttpErrorResponse) => {
                this.toastService.showError(getServerError(response));
                return EMPTY;
              }),
            );
        }),
        toArray(),
        tap((responses: GroupCustomSegment[][]) => {
          if (0 < responses.length) {
            const lastResponse = responses[responses.length - 1];
            this.store.dispatch(
              UpdateGroupSuccess({
                group: {
                  ...this.group,
                  [this.isPrivateSegment
                    ? 'hidden_custom_segments'
                    : 'custom_segments']: lastResponse,
                },
              }),
            );
            this.closeDrawer(lastResponse);
            return;
          }
          this.toastService.showError(
            $localize`:@@populateSegmentValueDrawer.errorMessage:All segment updates failed.`,
          );
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  closeDrawer(response?: GroupCustomSegment[]) {
    this.drawerRef.close(response);
  }
}
